import DigiobcanApi, { type Issue } from '../../../../api/DigiobcanApi';
import { userLabel } from '../../../../components/UserAvatarStack';
import { getReportCategoryUsers } from '../../../../features/queries/getReportCategoryUsers';
import { useM, useQ } from '../../../../lib/query';
import { useLogger } from '../../../../providers/LoggerProvider';
import { __, toastError, toastSuccess } from '../../../../utils';
import { SectionHeading } from './IssueSection';
import {
  Box,
  SelectInput,
  Stack,
  Typography,
} from '@presov.sk/presov-ui-react';
import React from 'react';

type Props = {
  issue: Issue;
  compact?: boolean;
  onDone: () => Promise<unknown>;
};

const ERRORS: Record<string, string> = {
  ASSIGNEE_NOT_MEMBER: __(
    'Vybraný kolega už nie je členom kategórie. Obnovte stránku.',
  ),
  ISSUE_NOT_ASSIGNABLE: __('Vyriešený ani zlúčený nález sa už neprideľuje.'),
  CATEGORY_NOT_ASSIGNING: __('Kategória už nefunguje na prideľovaní.'),
  FORBIDDEN_ISSUE_ASSIGN: __('V tejto kategórii podnety nerozdeľujete.'),
  REPORT_ARCHIVED: __('Archivovaný podnet sa neprideľuje.'),
};

/**
 * Riešiteľ nálezu v kategórii, ktorá funguje na prideľovaní (moj-presov#17).
 *
 * Rozdeľovateľ (`can_assign` alebo správca) tu vyberá zo **členov kategórie**
 * - inak to API ani nedovolí (`ASSIGNEE_NOT_MEMBER`). Či smie, počíta server
 * (`issue.can_assign`), nie appka: zahŕňa to aj `access-all` a stav nálezu.
 * Zmena sa ukladá hneď po výbere, mimo pätičky s rozhodnutím - pridelenie nie
 * je rozhodnutie o náleze a nemá čakať na „Potvrdiť".
 *
 * Natívny select a nie vyhľadávanie: členov kategórie je pár desiatok najviac
 * a na mobile otvorí systémový vyberač.
 */
const IssueAssignee: React.FC<Props> = ({ issue, compact = false, onDone }) => {
  const { logger } = useLogger();
  // Archivovaný podnet a kategóriu, ktorú úradník len vidí, vylučuje
  // volajúci - komponent sa tam vôbec nekreslí.
  const editable = !!issue.can_assign;

  const { data: members = [], isLoading } = useQ(
    getReportCategoryUsers,
    { categoryId: issue.category_id },
    { enabled: editable },
  );

  const assignMutation = useM(
    (userId: string) =>
      userId
        ? DigiobcanApi.adminAssignIssue(issue.id, {}, { user_id: userId })
        : DigiobcanApi.adminUnassignIssue(issue.id),
    {
      onSuccess: async (_res, userId) => {
        await onDone();
        toastSuccess(
          userId
            ? __('Nález bol pridelený.')
            : __('Pridelenie nálezu bolo zrušené.'),
        );
      },
      onError: (e, userId) => {
        logger.error('Error assigning issue', {
          action: 'adminAssignIssue',
          error: e,
          data: { issueId: issue.id, userId },
        });
        toastError(
          ERRORS[e.details?.name ?? ''] ??
            __('Pridelenie sa nepodarilo uložiť.'),
        );
      },
    },
  );

  // Bez práva meniť riešiteľa ho karta ukazuje len v pásiku pod hlavičkou.
  if (!editable) {
    return null;
  }

  // Pridelený kolega musí byť v ponuke, aj keby sa zoznam členov ešte
  // načítaval - inak by select ukázal „Nepridelené" a klamal by.
  const options = [
    { value: '', label: __('Nepridelené') },
    ...members.map((member) => ({
      value: member.id,
      label:
        [member.name, member.surname].filter(Boolean).join(' ') ||
        member.email ||
        member.id,
    })),
    ...(issue.assignee && !members.some((m) => m.id === issue.assignee!.id)
      ? [{ value: issue.assignee.id, label: userLabel(issue.assignee) }]
      : []),
  ];

  const hint = issue.assignee
    ? __('Podnet vidí a rieši len pridelený kolega a rozdeľovatelia.')
    : __(
        'Kým nález nikomu nepridelíte, ostatní členovia kategórie ho nevidia.',
      );

  return (
    <Stack
      direction={compact ? 'column' : 'row'}
      alignItems={compact ? 'stretch' : 'center'}
      gap={compact ? 0.75 : 3}
    >
      {compact ? (
        <Stack gap={0.25} sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant='body2' fontWeight={500}>
            {__('Riešiteľ')}
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            {hint}
          </Typography>
        </Stack>
      ) : (
        // Na desktope je riešiteľ vlastná karta sekcie ([[IssueSection]]),
        // nadpis má preto jej tvar.
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <SectionHeading title={__('Riešiteľ')} description={hint} />
        </Box>
      )}
      <SelectInput
        name='assignee'
        aria-label={__('Riešiteľ')}
        value={issue.assignee?.id ?? ''}
        options={options}
        disabled={isLoading || assignMutation.isPending}
        onChange={(event) => {
          if (!navigator.onLine) {
            toastError(__('Nie ste pripojený k internetu.'));
            return;
          }
          assignMutation.mutate(event.target.value);
        }}
        style={compact ? undefined : { width: 320 }}
      />
    </Stack>
  );
};

export default IssueAssignee;
